import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EMOTION_PAIRS } from "../data/courseData";
import { Slider } from "./ui/slider";
import { Textarea } from "./ui/textarea";
import { playBreathChime, unlockAudio } from "../lib/audio";
import { useSoundPref } from "../hooks/useSoundPref";
import { Volume2, VolumeX } from "lucide-react";

export default function JournalNeutralization({ prompts = [], onComplete }) {
  // Phases: 0..n-1 = prompts, then 4 neutralization steps (n..n+3), then equalize (n+4)
  const promptCount = prompts.length;
  const lastIndex = promptCount + 4; // 0..promptCount-1 prompts, promptCount..promptCount+3 neutralization, promptCount+4 final
  const [phase, setPhase] = useState(0);
  const [responses, setResponses] = useState({});
  const [emotion, setEmotion] = useState(null);
  const [opposite, setOpposite] = useState(null);
  const [visualization, setVisualization] = useState("");
  const [intensityBefore, setIntensityBefore] = useState(7);
  const [intensityAfter, setIntensityAfter] = useState(3);

  function next() {
    if (phase < lastIndex) setPhase(phase + 1);
    else {
      onComplete({
        prompt_responses: prompts.map((p, i) => ({ prompt: p, response: responses[i] || "" })),
        emotion,
        opposite_emotion: opposite,
        visualization,
        intensity_before: intensityBefore,
        intensity_after: intensityAfter,
      });
    }
  }

  const canNext = (() => {
    if (phase < promptCount) return (responses[phase] || "").trim().length > 0;
    if (phase === promptCount) return !!emotion;
    if (phase === promptCount + 1) return !!opposite;
    if (phase === promptCount + 2) return visualization.trim().length > 0;
    return true;
  })();

  const totalPhases = lastIndex + 1;
  const progress = ((phase + 1) / totalPhases) * 100;

  return (
    <div className="space-y-6" data-testid="journal-flow">
      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-[#22D3EE]"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <AnimatePresence mode="wait">
        {phase < promptCount && (
          <motion.div key={`p${phase}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="space-y-4">
            <div className="bps-eyebrow">Prompt · {phase + 1} of {promptCount}</div>
            <h3 className="font-display text-2xl tracking-tight">{prompts[phase]}</h3>
            <Textarea
              value={responses[phase] || ""}
              onChange={(e) => setResponses((r) => ({ ...r, [phase]: e.target.value }))}
              placeholder="Write freely. No one is grading this."
              rows={8}
              data-testid="journal-textarea"
              className="bps-card border-0 p-5 text-[15px] leading-relaxed focus-visible:ring-2 focus-visible:ring-[#22D3EE]"
            />
          </motion.div>
        )}

        {phase === promptCount && (
          <motion.div key="emotion" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="space-y-4">
            <div className="bps-eyebrow">Neutralization · 1 of 4</div>
            <h3 className="font-display text-2xl tracking-tight">Identify the emotion you're carrying.</h3>
            <div className="grid grid-cols-2 gap-3">
              {EMOTION_PAIRS.map((e) => (
                <button
                  key={e.emotion}
                  onClick={() => setEmotion(e.emotion)}
                  data-testid={`emotion-${e.emotion.toLowerCase()}`}
                  className={`p-4 rounded-2xl border text-left transition-all ${
                    emotion === e.emotion
                      ? "border-[#22D3EE] bg-[#22D3EE]/10 text-[#22D3EE]"
                      : "border-white/10 hover:border-[#1C1C1E]/30"
                  }`}
                >
                  {e.emotion}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {phase === promptCount + 1 && (
          <motion.div key="opposite" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="space-y-4">
            <div className="bps-eyebrow">Neutralization · 2 of 4</div>
            <h3 className="font-display text-2xl tracking-tight">Choose the opposite, the feeling you want.</h3>
            <div className="grid grid-cols-2 gap-3">
              {EMOTION_PAIRS.map((e) => (
                <button
                  key={e.opposite}
                  onClick={() => setOpposite(e.opposite)}
                  data-testid={`opposite-${e.opposite.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`p-4 rounded-2xl border text-left transition-all ${
                    opposite === e.opposite
                      ? "border-[#22D3EE] bg-[#22D3EE]/10 text-[#22D3EE]"
                      : "border-white/10 hover:border-[#1C1C1E]/30"
                  }`}
                >
                  {e.opposite}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {phase === promptCount + 2 && (
          <motion.div key="vis" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="space-y-4">
            <div className="bps-eyebrow">Neutralization · 3 of 4</div>
            <h3 className="font-display text-2xl tracking-tight">Visualize what {opposite?.toLowerCase() || "this feeling"} looks like in your body.</h3>
            <Textarea
              value={visualization}
              onChange={(e) => setVisualization(e.target.value)}
              placeholder="Describe what your body feels like, what you see, what you carry…"
              rows={6}
              data-testid="visualization-textarea"
              className="bps-card border-0 p-5 text-[15px] leading-relaxed focus-visible:ring-2 focus-visible:ring-[#22D3EE]"
            />
          </motion.div>
        )}

        {phase === promptCount + 3 && (
          <motion.div key="before" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="space-y-5">
            <div className="bps-eyebrow">Neutralization · 4 of 4</div>
            <h3 className="font-display text-2xl tracking-tight">Equalize emotional intensity.</h3>
            <div className="bps-card p-5 space-y-4">
              <div>
                <div className="text-xs text-[#7C8FA8] uppercase tracking-[0.18em] mb-2">{emotion} intensity</div>
                <Slider value={[intensityBefore]} onValueChange={(v) => setIntensityBefore(v[0])} min={0} max={10} step={1} data-testid="intensity-before" />
                <div className="mt-1 text-sm text-white">{intensityBefore} / 10</div>
              </div>
              <div>
                <div className="text-xs text-[#7C8FA8] uppercase tracking-[0.18em] mb-2">{opposite} intensity</div>
                <Slider value={[intensityAfter]} onValueChange={(v) => setIntensityAfter(v[0])} min={0} max={10} step={1} data-testid="intensity-after" />
                <div className="mt-1 text-sm text-white">{intensityAfter} / 10</div>
              </div>
            </div>
          </motion.div>
        )}

        {phase === promptCount + 4 && (
          <motion.div key="final" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }} className="space-y-7 text-center py-4" data-testid="release-stage">
            <div>
              <div className="bps-eyebrow">Release</div>
              <h3 className="font-display text-3xl font-light tracking-tighter mt-2">Let it leave the body.</h3>
              <p className="text-sm text-[#9AAEC6] mt-3 max-w-xs mx-auto">Your nervous system needs a clear signal that <span className="text-white">{emotion}</span> is being released. Move with the breath below.</p>
            </div>

            <PacedBreathOrb />

            <ol className="text-left max-w-xs mx-auto space-y-3" data-testid="release-instructions">
              <li className="flex gap-3 text-sm">
                <span className="font-mono text-[11px] mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border border-[#22D3EE]/50 text-[#22D3EE]">1</span>
                <span className="text-white/90 leading-relaxed"><span className="text-[#22D3EE] font-medium">Inhale for 4.</span> Draw light into the place where <span className="font-medium">{emotion}</span> lives in your body.</span>
              </li>
              <li className="flex gap-3 text-sm">
                <span className="font-mono text-[11px] mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border border-[#22D3EE]/50 text-[#22D3EE]">2</span>
                <span className="text-white/90 leading-relaxed"><span className="text-[#22D3EE] font-medium">Hold for 4.</span> Name it silently: <span className="italic">"This is {emotion?.toLowerCase()}."</span> Let it be seen.</span>
              </li>
              <li className="flex gap-3 text-sm">
                <span className="font-mono text-[11px] mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border border-[#F59E0B]/55 text-[#F59E0B]">3</span>
                <span className="text-white/90 leading-relaxed"><span className="text-[#F59E0B] font-medium">Exhale for 8.</span> Picture <span className="font-medium">{emotion?.toLowerCase()}</span> leaving your body as smoke. Watch it dissolve into the air.</span>
              </li>
            </ol>

            <div className="bps-card bps-card-accent p-5 space-y-2">
              <div className="bps-eyebrow text-[#22D3EE]">Affirm</div>
              <p className="font-display text-lg leading-snug">I release <span className="text-[#F59E0B]">{emotion?.toLowerCase()}</span>.</p>
              <p className="font-display text-lg leading-snug">I welcome <span className="text-[#22D3EE]">{opposite?.toLowerCase()}</span>.</p>
              <p className="text-xs text-[#9AAEC6] italic mt-2">Repeat both lines aloud, slowly, twice. Then save when ready.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={next}
        disabled={!canNext}
        data-testid="journal-next"
        className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
      >
        {phase === lastIndex ? "Save Entry" : "Continue"}
      </button>
    </div>
  );
}

/* ---------- Paced 4–4–8 Breathing Orb ---------- */
// One cycle = 16 s: 4 s inhale → 4 s hold → 8 s exhale.
// Scale + opacity are derived from real elapsed time so the visual matches the clock.
function PacedBreathOrb() {
  const [tick, setTick] = useState(0);
  const [soundOn, setSoundOn] = useSoundPref();
  const prevPhaseRef = useRef(null);

  useEffect(() => {
    // Unlock the audio context on first mount — this stage is reached via user clicks
    if (soundOn) unlockAudio();
    const start = Date.now();
    const id = setInterval(() => {
      setTick(((Date.now() - start) / 1000) % 16);
    }, 80);
    return () => clearInterval(id);
  }, [soundOn]);

  let phase, remaining, scale, opacity, color;
  if (tick < 4) {
    phase = "INHALE";
    remaining = Math.max(1, Math.ceil(4 - tick));
    const p = tick / 4;
    scale = 0.62 + p * 0.5;       // 0.62 → 1.12
    opacity = 0.5 + p * 0.5;      // 0.5  → 1.0
    color = "rgba(34,211,238,0.78)";
  } else if (tick < 8) {
    phase = "HOLD";
    remaining = Math.max(1, Math.ceil(8 - tick));
    scale = 1.12;
    opacity = 1;
    color = "rgba(255,255,255,0.85)";
  } else {
    phase = "EXHALE";
    remaining = Math.max(1, Math.ceil(16 - tick));
    const p = (tick - 8) / 8;
    scale = 1.12 - p * 0.5;       // 1.12 → 0.62
    opacity = 1 - p * 0.5;        // 1.0  → 0.5
    color = "rgba(245,158,11,0.7)";
  }

  // Play a soft chime at every phase transition (if sound is on).
  // We use a ref to compare with the previous phase across renders so each
  // transition fires exactly once.
  useEffect(() => {
    if (prevPhaseRef.current !== null && prevPhaseRef.current !== phase) {
      if (soundOn) playBreathChime(phase);
    }
    prevPhaseRef.current = phase;
  }, [phase, soundOn]);

  return (
    <div className="relative mx-auto w-52 h-52 flex items-center justify-center" data-testid="paced-breath-orb">
      {/* Guide rings (static) */}
      <div className="absolute inset-0 rounded-full border border-[#22D3EE]/15" />
      <div className="absolute inset-4 rounded-full border border-[#22D3EE]/10" />
      <div className="absolute inset-10 rounded-full border border-[#22D3EE]/8" />

      {/* Animated orb */}
      <div
        className="absolute rounded-full"
        style={{
          width: "82%",
          height: "82%",
          transform: `scale(${scale})`,
          opacity,
          background: `radial-gradient(circle at 50% 38%, ${color}, rgba(34,211,238,0.12) 55%, transparent 75%)`,
          boxShadow: phase === "HOLD" ? `0 0 60px ${color}` : `0 0 40px ${color}`,
          transition: "transform 90ms linear, opacity 90ms linear, box-shadow 200ms ease-out, background 200ms ease-out",
        }}
      />

      {/* Center labels */}
      <div className="relative text-center">
        <div
          className="font-mono text-[10px] tracking-[0.32em] uppercase"
          data-testid="breath-phase"
          style={{
            color: phase === "INHALE" ? "#22D3EE" : phase === "EXHALE" ? "#F59E0B" : "rgba(255,255,255,0.9)",
          }}
        >
          {phase}
        </div>
        <div className="font-display text-5xl font-extralight text-white mt-1 leading-none tabular-nums" data-testid="breath-count">{remaining}</div>
        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40 mt-2">4 · 4 · 8</div>
      </div>
    </div>
  );
}
